import { Case, CaseOpen, QueueConfiguration, ReleaseCaseRequest } from '.';
import { QueueStats, UserStat, DefaultUxPreferences, TenantFull } from './queue.model';
import axios, { AxiosResponse } from 'axios';

import { AuthProvider } from './auth.service';
import { Feedback } from './feedback.model';
import { environment } from '../environment';
import { CaseAction } from './action.model';
import { CasePoints } from './points.model';
import { CaseSummary } from 'services';

const apiUrl: string = environment.apiUrl;
const getConfigsForUserRoute = apiUrl + 'api/queue/config';
const getQueueStatsRoute = apiUrl + 'api/queue/stats';

const postLookupCase = apiUrl + 'api/case/lookup';
const postAssignCases = apiUrl + 'api/case/assign';
const postUnAssignCases = apiUrl + 'api/case/unassign';
const postClaimCase = apiUrl + 'api/case/claim';
const postUndoCase = apiUrl + 'api/case/undo';
const postCaseSearch = apiUrl + 'api/case/search';

const postLookupFeedback = apiUrl + 'api/feedback/lookup';
const postLookupAction = apiUrl + 'api/action/lookup';
const postLookupPoints = apiUrl + 'api/v1/stacking/lookup';

const getUserIsSystemAdminRoute = apiUrl + 'api/user/isSystemAdmin';
const getUserIsTenantAdminRoute = (tenantId: string) => `${apiUrl}api/user/isTenantAdmin/tenantId/${tenantId}`;

const isMock: boolean = JSON.parse(environment.useMock) as boolean;

const getTenantInfoRoute = (tenantId: string): string => {
  return `${apiUrl}api/tenant/${tenantId}`;
};

const getQueueConfigRoute = (tenantId: string, titleId: string, subjectType: string): string => {
  return `${getConfigsForUserRoute}/tenantId/${tenantId}/titleId/${titleId}/subjectType/${subjectType}`;
};

export async function getQueueConfig(
  tenantId: string,
  titleId: string,
  subjectType: string
): Promise<QueueConfiguration> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<QueueConfiguration> = await axios.get(
    getQueueConfigRoute(tenantId, titleId, subjectType)
  );

  return response.data;
}

export async function getConfigsForUser(): Promise<QueueConfiguration[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<QueueConfiguration[]> = await axios.get(getConfigsForUserRoute);

  return response.data.map((config: QueueConfiguration) => {
    // Set default UxPreference
    config.uxPreferences = { ...DefaultUxPreferences, ...config.uxPreferences };
    config.uxPreferences.decide = { ...DefaultUxPreferences.decide, ...config.uxPreferences.decide };
    config.uxPreferences.feedback = { ...DefaultUxPreferences.feedback, ...config.uxPreferences.feedback };
    config.uxPreferences.subjectImage = { ...DefaultUxPreferences.subjectImage, ...config.uxPreferences.subjectImage };
    return config;
  });
}

export async function getQueueStats(config: QueueConfiguration): Promise<QueueStats> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const requestRoute = `${getQueueStatsRoute}/tenant/${config.tenant.tenantId}/title/${config.title.titleId}/subject/${config.subjectType}`;
  const response: AxiosResponse<QueueStats> = await axios.get(requestRoute);
  return response.data;
}

export async function postReviewer(config: QueueConfiguration, id: string | undefined): Promise<UserStat> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  let requestRoute;
  if (id === undefined) {
    requestRoute = `${getQueueStatsRoute}/tenant/${config.tenant.tenantId}/title/${config.title.titleId}/subject/${config.subjectType}/reviewer`;
  } else {
    requestRoute = `${getQueueStatsRoute}/tenant/${config.tenant.tenantId}/title/${config.title.titleId}/subject/${config.subjectType}/id/${id}/reviewer`;
  }
  const response: AxiosResponse<UserStat> = await axios.post(requestRoute);
  return response.data;
}

export async function deleteReviewer(config: QueueConfiguration, id: string): Promise<UserStat> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const requestRoute = `${getQueueStatsRoute}/tenant/${config.tenant.tenantId}/title/${config.title.titleId}/subject/${config.subjectType}/id/${id}/reviewer`;
  const response: AxiosResponse<UserStat> = await axios.delete(requestRoute);
  return response.data;
}

export async function postUpsertQueueConfig(queueData: QueueConfiguration): Promise<QueueConfiguration> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<QueueConfiguration> = await axios.post(apiUrl.concat('api/queue/upsert'), queueData);

  return response.data;
}

export async function lookupCase(
  tenantId: string | null,
  titleId: string | null,
  subjectType: string | null,
  subjectId: string | null,
  caseId: string | null
): Promise<Case> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<Case> = await axios.post(postLookupCase, {
    tenantId: tenantId,
    titleId: titleId,
    subjectType: subjectType,
    subjectId: subjectId,
    caseId: caseId
  });
  return response.data;
}

export async function assignCases(config: QueueConfiguration): Promise<Case[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<Case[]> = await axios.post(postAssignCases, {
    tenantId: config.tenant.tenantId,
    titleId: config.title.titleId,
    subjectType: config.subjectType
  });
  return response.data;
}

export async function claimCase(config: QueueConfiguration, caseId: string): Promise<Case> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<Case> = await axios.post(postClaimCase, {
    tenantId: config.tenant.tenantId,
    titleId: config.title.titleId,
    subjectType: config.subjectType,
    caseId: caseId
  });
  return response.data;
}

export async function unassignCases(request: ReleaseCaseRequest): Promise<CaseOpen> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<CaseOpen> = await axios.post(postUnAssignCases, request);
  return response.data;
}

export async function overturnCase(request: Feedback): Promise<Feedback> {
  if (isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<Feedback> = await axios.post(postUndoCase, request);
  return response.data;
}

export async function getCaseFeedback(
  tenantId: string,
  titleId: string,
  subjectType: string,
  subjectId: string,
  caseId: string
): Promise<Feedback[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const response: AxiosResponse<Feedback[]> = await axios.post(postLookupFeedback, {
    tenantId: tenantId,
    titleId: titleId,
    subjectType: subjectType,
    subjectId: subjectId,
    caseId: caseId
  });
  return response.data;
}

export async function getCasePoints(
  tenantId: string,
  titleId: string,
  userType: string,
  userId: string,
  caseId: string
): Promise<CasePoints> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<CasePoints> = await axios.post(postLookupPoints, {
    tenantId: tenantId,
    titleId: titleId,
    userType: userType,
    userId: userId,
    caseId: caseId
  });
  return response.data;
}

export async function getCaseAction(
  tenantId: string,
  titleId: string,
  subjectType: string,
  subjectId: string,
  caseId: string
): Promise<CaseAction[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<CaseAction[]> = await axios.post(postLookupAction, {
    tenantId: tenantId,
    titleId: titleId,
    subjectType: subjectType,
    subjectId: subjectId,
    caseId: caseId
  });
  return response.data;
}

export async function caseSearch(tenantId: string, search: string): Promise<CaseSummary[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<CaseSummary[]> = await axios.post(postCaseSearch, {
    tenantId: tenantId,
    searchTerm: search
  });
  return response.data;
}

export async function getTenantInfo(tenantId: string): Promise<TenantFull> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<TenantFull> = await axios.get(getTenantInfoRoute(tenantId));
  return response.data;
}

export async function getUserIsSystemAdmin(): Promise<boolean> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<boolean> = await axios.get(getUserIsSystemAdminRoute);
  return response.data;
}

export async function getUserIsTenantAdmin(tenantId: string): Promise<boolean> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<boolean> = await axios.get(getUserIsTenantAdminRoute(tenantId));
  return response.data;
}
